<script setup lang="ts">
import { ModalDialog, PrimaryButton, router } from '@hfc/ui-core';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { PlanResponse } from '@/planList/domain/PlanResponse';
import PlanDetails from '@/planList/infrastructure/components/PlanDetails.vue';
import PlanList from '@/planList/infrastructure/components/PlanList.vue';
import { Quote } from '@/application/shared/domain/Quote';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { usePlanStore } from '@/planList/infrastructure/composition/usePlanStore';
import { useDentalAndVisionApplicationStore } from '@/application/shared/infrastructure/composition/useDentalAndVisionApplicationStore';

const { appStore } = useAppStore();

const { planStore, planState } = usePlanStore();

const { dentalAndVisionApplicationStore } = useDentalAndVisionApplicationStore();

const { getRouteNameByAppType } = useUrlHelper();
const { handleSfApiError } = useHandleApiError();

const request = ref({} as Quote);

const props = defineProps<{
  applicationType: AppTypeDentalOrVision;
  quoteId: number;
}>();

const showDetailsModal = ref<boolean>(false);
const modalDetailsPlan = ref<PlanResponse>({} as PlanResponse);

const quoteRouteName = getRouteNameByAppType(props.applicationType, 'Quote');
const applicationRouteName = getRouteNameByAppType(props.applicationType, 'Application');

const setSelectedPlan = async (plan: PlanResponse): Promise<void> => {
  dentalAndVisionApplicationStore.resetLocalStorage(props.applicationType);
  await planStore.updateSelectedPlan(plan, props.applicationType);
  router.push({ name: applicationRouteName });
};

const setViewDetails = (plan: PlanResponse): void => {
  modalDetailsPlan.value = plan;
  showDetailsModal.value = true;
};

const closeModalMethod = (): void => {
  showDetailsModal.value = false;
  modalDetailsPlan.value = {} as PlanResponse;
};

const getQuoteData = async (): Promise<void> => {
  request.value = await planStore.getQuoteData(props.applicationType);
  if (Object.keys(request.value).length === 0) {
    router.push({ name: quoteRouteName });
  }
};

const getPlanList = async (): Promise<void> => {
  const { error } = props.applicationType === APPLICATION_TYPES.DENTAL ? await planStore.getDentalPlanList(request.value) : await planStore.getVisionPlanList(request.value);
  if (error) {
    handleSfApiError(error);
  }
};

onMounted(async () => {
  await getQuoteData();
  await getPlanList();
  appStore.updateCustomTitle(`Your ${props.applicationType} quotes`);
});

onBeforeUnmount(() => {
  appStore.updateCustomTitle('');
});

</script>
<template>
  <div class="mb-10 md:mb-0">
    <PlanList
      :application-type="applicationType"
      :plan-list="planStore.state.plans"
      :loading-plans="planState.loadingPlans"
      :applicants="request.applicants"
      :view-details-button="true"
      @selectPlan="setSelectedPlan"
      @viewDetails="setViewDetails" />
  </div>
  <ModalDialog v-if="showDetailsModal == true && modalDetailsPlan" @close="closeModalMethod()" confirm-button="Select plan" :confirm-button-type="PrimaryButton" size="xl" @confirm="() => setSelectedPlan(modalDetailsPlan)">
    <template #content>
      <PlanDetails v-if="modalDetailsPlan" :plan="modalDetailsPlan" :application-type="applicationType" />
    </template>
  </ModalDialog>
</template>
